/**
 * @file sheets/shared/drag-handle.tsx
 *
 * Visual drag handle for Bottom Sheet components.
 *
 * MD3 spec:
 * - Padding top/bottom: 22dp ("Drag handle padding top/bottom: 22dp")
 * - Width: 32dp, Height: 4dp, corner-full radius
 * - Color: on-surface-variant @ 40% opacity
 * - Touch target: top 48dp of sheet is interactive (py-[22px])
 * - When interactive: renders as `<button>`, Tab-focusable, Space/Enter cycles snap points
 * - When decorative: purely visual, no semantics
 *
 * @see https://m3.material.io/components/sheets-bottom/guidelines#handle
 * @see https://m3.material.io/components/sheets-bottom/accessibility
 */

import * as React from "react";
import { cn } from "../../../lib/utils";
import type { DragHandleProps } from "./types";

const INDICATOR = "h-1 w-8 rounded-full bg-m3-on-surface-variant/40";

export const DragHandle = React.forwardRef<HTMLDivElement, DragHandleProps>(
	({ className, onCycle, "aria-label": ariaLabel }, ref) => {
		const isInteractive = Boolean(onCycle);

		if (isInteractive) {
			/**
			 * Interactive variant: native `<button>` wrapping the indicator bar.
			 * Using `<button>` satisfies both `noStaticElementInteractions` and
			 * `useAriaPropsSupportedByRole` lint rules without any overrides.
			 */
			return (
				<button
					ref={ref as unknown as React.Ref<HTMLButtonElement>}
					type="button"
					aria-label={ariaLabel ?? "Adjust sheet height"}
					className={cn(
						"flex items-center justify-center w-full pt-[30px] pb-[22px] shrink-0",
						"cursor-pointer bg-transparent border-0",
						"rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-m3-on-surface-variant focus-visible:ring-offset-2",
						className,
					)}
					onClick={onCycle}
					data-testid="sheet-drag-handle"
				>
					<div className={INDICATOR} />
				</button>
			);
		}

		/**
		 * Decorative variant: plain `<div>`, no semantics.
		 * Purely visual — does not appear in the accessibility tree.
		 */
		return (
			<div
				ref={ref}
				aria-hidden
				className={cn(
					"flex items-center justify-center w-full pt-[30px] pb-[22px] shrink-0",
					className,
				)}
				data-testid="sheet-drag-handle"
			>
				<div className={INDICATOR} />
			</div>
		);
	},
);

DragHandle.displayName = "DragHandle";
